import React from "react";
import { Link, useLocation } from "react-router-dom";
import { musicTherapySyllabus } from "../mock/musicTherapySyllabus";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Download, ArrowLeft, Heart } from "lucide-react";

export default function MusicTherapySyllabus() {
  const location = useLocation();
  const fromDashboard = location.state?.fromDashboard || document.referrer.includes('/dashboard');

  const handleSavePdf = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const marginX = 48;
    let cursorY = 60;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Global Music Conservatory", marginX, cursorY);

    cursorY += 20;
    doc.setFontSize(12);
    doc.text(musicTherapySyllabus.title, marginX, cursorY);

    cursorY += 18;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.text(musicTherapySyllabus.eligibility, marginX, cursorY);

    cursorY += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const addWrappedText = (text, x, y, maxWidth) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return y + lines.length * 13;
    };

    cursorY = addWrappedText(musicTherapySyllabus.description, marginX, cursorY, 500) + 12;

    // Topics
    doc.setFont("helvetica", "bold");
    doc.text("Syllabus Topics:", marginX, cursorY);
    cursorY += 16;

    doc.setFont("helvetica", "normal");
    musicTherapySyllabus.topics.forEach((topic) => {
      if (cursorY > 760) {
        doc.addPage();
        cursorY = 60;
      }
      cursorY = addWrappedText(`• ${topic}`, marginX + 10, cursorY, 490);
    });

    // Grading Guidelines
    doc.addPage();
    cursorY = 60;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(musicTherapySyllabus.gradingGuidelines.title, marginX, cursorY);
    cursorY += 16;
    doc.setFontSize(10);
    doc.text(musicTherapySyllabus.gradingGuidelines.subtitle, marginX, cursorY);
    cursorY += 20;

    musicTherapySyllabus.gradingGuidelines.sections.forEach((section) => {
      if (cursorY > 700) {
        doc.addPage();
        cursorY = 60;
      }

      doc.setFont("helvetica", "bold");
      doc.text(`${section.number}. ${section.title}`, marginX, cursorY);
      cursorY += 14;

      doc.setFont("helvetica", "normal");
      section.items.forEach((item) => {
        doc.text(`• ${item.name} – ${item.marks} marks`, marginX + 14, cursorY);
        cursorY += 12;
      });

      if (section.assessmentType) {
        doc.setFont("helvetica", "italic");
        doc.text(`Assessment Type: ${section.assessmentType}`, marginX + 14, cursorY);
        cursorY += 18;
      } else {
        cursorY += 8;
      }
    });

    // Passing Criteria
    cursorY += 10;
    doc.setFont("helvetica", "bold");
    doc.text(musicTherapySyllabus.gradingGuidelines.passingCriteria.title, marginX, cursorY);
    cursorY += 14;
    doc.setFont("helvetica", "normal");
    doc.text(`• ${musicTherapySyllabus.gradingGuidelines.passingCriteria.minimumPassing}`, marginX + 10, cursorY);
    cursorY += 12;
    doc.text(`• ${musicTherapySyllabus.gradingGuidelines.passingCriteria.practicalMinimum}`, marginX + 10, cursorY);
    cursorY += 20;

    // Grade Scale
    doc.setFont("helvetica", "bold");
    doc.text("Grade Scale", marginX, cursorY);
    cursorY += 14;
    doc.setFont("helvetica", "normal");
    musicTherapySyllabus.gradingGuidelines.gradeScale.forEach((grade) => {
      doc.text(`• ${grade.range}: ${grade.grade}`, marginX + 10, cursorY);
      cursorY += 12;
    });

    const fileName = "Professional-Music-Therapy-Certification-Syllabus.pdf";
    doc.save(fileName);
  };

  return (
    <div className="space-y-8">
      {fromDashboard && (
        <Link to="/dashboard">
          <Button variant="outline" size="sm" className="border-[#d5c39d] text-[#182033] hover:bg-[#f5ebd9]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </Link>
      )}

      <section className="flex flex-wrap items-center justify-between gap-3 border-b border-[#ddcaa4] pb-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1b2236] text-[#f8f2e6]">
              <Heart className="h-6 w-6" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8c7648]">
                Professional Certification
              </p>
              <h1 className="text-xl font-semibold tracking-tight text-[#182033]">
                {musicTherapySyllabus.title}
              </h1>
            </div>
          </div>
          <p className="max-w-2xl text-sm leading-relaxed text-[#4b4a46]">
            {musicTherapySyllabus.description}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="border-[#d0bf99] bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1f2738] hover:bg-[#f5ebd9]"
          >
            <Link to="/instruments-levels">Back to Instruments</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSavePdf}
            className="inline-flex items-center gap-1 border-[#d0bf99] bg-[#1b2236] px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f8f2e6] hover:bg-[#151b2b]"
          >
            <Download className="h-3 w-3" />
            <span>Save as PDF</span>
          </Button>
        </div>
      </section>

      {/* Eligibility Notice */}
      <section className="rounded-xl border border-amber-300 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-800">
          📋 Eligibility: {musicTherapySyllabus.eligibility}
        </p>
      </section>

      {/* Syllabus Topics */}
      <section>
        <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold tracking-tight text-[#182033]">
              Syllabus Topics
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ul className="grid gap-2 sm:grid-cols-2">
              {musicTherapySyllabus.topics.map((topic, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-[#4b4a46]">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#8c7648]"></span>
                  {topic}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Grading Guidelines */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight text-[#182033]">
          {musicTherapySyllabus.gradingGuidelines.title}
        </h2>
        <p className="text-sm text-[#6b6a66]">{musicTherapySyllabus.gradingGuidelines.subtitle}</p>

        <div className="grid gap-4 lg:grid-cols-2">
          {musicTherapySyllabus.gradingGuidelines.sections.map((section) => (
            <Card key={section.number} className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold tracking-tight text-[#182033]">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1b2236] text-xs text-[#f8f2e6]">
                    {section.number}
                  </span>
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <ul className="space-y-1">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex justify-between text-sm">
                      <span className="text-[#4b4a46]">{item.name}</span>
                      <span className="font-semibold text-[#8c7648]">{item.marks} marks</span>
                    </li>
                  ))}
                </ul>
                {section.assessmentType && (
                  <p className="text-xs italic text-[#6b6a66] border-t border-[#e1d2b3] pt-2">
                    Assessment Type: {section.assessmentType}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Passing Criteria & Grade Scale */}
      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="border-[#c9a227]/40 bg-gradient-to-br from-[#fffdf5] to-[#f9f2e6] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
              {musicTherapySyllabus.gradingGuidelines.passingCriteria.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            <p className="text-sm text-[#4b4a46]">
              • {musicTherapySyllabus.gradingGuidelines.passingCriteria.minimumPassing}
            </p>
            <p className="text-sm text-[#4b4a46]">
              • {musicTherapySyllabus.gradingGuidelines.passingCriteria.practicalMinimum}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
              Grade Scale
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold uppercase text-[#1f2738]">Marks Range</TableHead>
                  <TableHead className="text-xs font-semibold uppercase text-[#1f2738]">Grade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {musicTherapySyllabus.gradingGuidelines.gradeScale.map((grade, idx) => (
                  <TableRow key={idx} className="text-sm">
                    <TableCell className="text-[#4b4a46]">{grade.range}</TableCell>
                    <TableCell className={`font-medium ${grade.grade === "Fail" ? "text-red-600" : grade.grade === "Distinction" ? "text-green-700" : "text-[#182033]"}`}>
                      {grade.grade}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </section>

      {/* About Section */}
      <section className="rounded-2xl border border-[#ddcaa4] bg-[#f9f2e6] p-4 lg:p-6">
        <h2 className="mb-3 text-base font-semibold tracking-tight text-[#182033]">
          About This Certification
        </h2>
        <div className="space-y-2 text-sm leading-relaxed text-[#4b4a46]">
          <p>
            The Professional Music Therapy Certification prepares candidates to use music as a therapeutic 
            tool for improving emotional, cognitive, and behavioral wellbeing of individuals.
          </p>
          <p>
            This certification covers the theoretical foundations of music therapy, practical techniques, 
            ethical standards, and professional practice, qualifying graduates to work as certified 
            music therapy practitioners.
          </p>
        </div>
      </section>
    </div>
  );
}
